import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  doc,
  getDoc,
  limit,
  startAfter,
  getCountFromServer,
  getDocsFromCache,
  onSnapshot
} from 'firebase/firestore';
import { db, auth } from '../../../firebaseConfig.js';
import { fetchUsersByIds } from '../../slices/usersCacheSlice.js';
import { setUnsubscribe } from '../solutionSlice.js';
import { normalizeSolutionData, getMillisFromTimestamp } from './utils.js';

/**
 * Fetch solutions for a specific post
 */
export const fetchSolutionsByPostId = createAsyncThunk(
  'solutions/fetchByPostId',
  async (postId, { dispatch, getState }) => {
    try {
      if (!postId) throw new Error('Post ID is required');

      const solutionsRef = collection(db, 'solutions');
      const q = query(
        solutionsRef,
        where('postId', '==', postId),
        orderBy('createdAt', 'desc')
      );
      const disableRealtime = process.env.REACT_APP_DISABLE_SOLUTION_LISTENERS === 'true' || process.env.REACT_APP_LOW_QUOTA_MODE === 'true';

      // Unsubscribe from any existing subscription
      const { solutions: { subscriptions } } = getState();
      if (subscriptions[postId]) {
        subscriptions[postId]();
        dispatch(setUnsubscribe({ postId, unsubscribe: null }));
      }

      if (!disableRealtime) {
        // Set up real-time subscription
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const solutions = [];
          querySnapshot.forEach((doc) => {
            const solution = normalizeSolutionData(doc);
            if (solution) solutions.push(solution);
          });
          // Update state with new solutions
          dispatch({
            type: 'solutions/updateSolutions',
            payload: { postId, solutions }
          });
        });
        // Store unsubscribe function
        dispatch(setUnsubscribe({ postId, unsubscribe }));
      }

      // Return initial data with cache-first
      try {
        const cached = await getDocsFromCache(q);
        if (!cached.empty) {
          return cached.docs.map(doc => normalizeSolutionData(doc)).filter(Boolean);
        }
      } catch (_) { /* cache miss */ }

      // If realtime disabled or cache empty, try server fetch (may fail if quota is exhausted)
      if (disableRealtime) {
        try {
          const querySnapshot = await getDocs(q);
          return querySnapshot.docs.map(doc => normalizeSolutionData(doc)).filter(Boolean);
        } catch (e) {
          // Gracefully degrade when quota exhausted
          console.warn('[fetchSolutionsByPostId] Server fetch skipped/failing under low quota mode:', e?.code || e?.message || e);
          return [];
        }
      } else {
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => normalizeSolutionData(doc)).filter(Boolean);
      }

    } catch (error) {
      console.error('Error fetching solutions:', error);
      throw error;
    }
  }
);

/**
 * Fetch a single solution by ID
 */
export const fetchSolutionById = createAsyncThunk(
  'solutions/fetchById',
  async (solutionId, { rejectWithValue }) => {
    try {
      if (!solutionId) throw new Error('Solution ID is required');

      const solutionRef = doc(db, 'solutions', solutionId);
      const docSnap = await getDoc(solutionRef);

      if (!docSnap.exists()) {
        throw new Error('Solution not found');
      }

      return normalizeSolutionData(docSnap);

    } catch (error) {
      console.error('Error fetching solution:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch solutions saved by a specific user
 */
export const fetchSavedSolutions = createAsyncThunk(
  'solutions/fetchSaved',
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) throw new Error('User ID is required');

      const solutionsRef = collection(db, 'solutions');
      const q = query(
        solutionsRef,
        where('savers', 'array-contains', userId),
        orderBy('updatedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const solutions = querySnapshot.docs.map(doc => normalizeSolutionData(doc)).filter(Boolean);

      return solutions;

    } catch (error) {
      console.error('Error fetching saved solutions:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Paginated: Fetch first page of solutions for a specific post
 */
export const fetchSolutionsFirstPage = createAsyncThunk(
  'solutions/fetchFirstPage',
  async ({ postId, pageSize = 10, forceCount = false }, { getState, rejectWithValue, dispatch }) => {
    try {
      if (!postId) throw new Error('Post ID is required');

      // Ensure fresh auth context before server reads
      try { await auth?.currentUser?.getIdToken(true); } catch (_) { }

      const solutionsRef = collection(db, 'solutions');
      // Use deterministic ordering to support startAfter with field values
      const q = query(
        solutionsRef,
        where('postId', '==', postId),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );

      // Low quota mode flag: skip server-heavy operations (unless forced to server)
      const forceServer = process.env.REACT_APP_FORCE_SERVER_SOLUTION_READS === 'true';
      let lowQuota = process.env.REACT_APP_LOW_QUOTA_MODE === 'true' || process.env.REACT_APP_DISABLE_SOLUTION_LISTENERS === 'true';
      if (forceServer) lowQuota = false;

      if (lowQuota) {
        // Cache-first: try to read from cache and avoid any server reads/counts
        try {
          const cachedSnap = await getDocsFromCache(q);
          if (!cachedSnap.empty) {
            const cachedDocs = cachedSnap.docs || [];
            const cachedSolutions = cachedDocs.map(d => normalizeSolutionData(d)).filter(Boolean);
            try {
              console.debug('[fetchSolutionsFirstPage] cache snapshot', {
                postId,
                lowQuota,
                forceServer,
                fromCache: cachedSnap?.metadata?.fromCache ?? 'unknown',
                size: cachedDocs.length
              });
            } catch (_) { }
            // In low quota, avoid paging/counting to minimize reads
            const total = cachedSolutions.length;
            return { postId, solutions: cachedSolutions, lastCursor: null, hasMore: false, pageSize, total };
          }
        } catch (_) {
          // Cache miss or cache disabled; fall through to return safe defaults without server reads
        }
        // No cache available; return safe defaults without hitting the server
        return { postId, solutions: [], lastCursor: null, hasMore: false, pageSize, total: 0 };
      }
      // Normal mode: include total count and server fetch
      // Fetch total count only when not disabled via env flag unless forceCount
      const disableCounts = ((process.env.REACT_APP_DISABLE_SOLUTION_COUNTS === 'true') && !forceCount) || lowQuota;
      let total = undefined;
      if (!disableCounts) {
        const countQ = query(
          solutionsRef,
          where('postId', '==', postId)
        );
        const countSnap = await getCountFromServer(countQ);
        total = Number(countSnap.data().count || 0);
      }

      // Fetch first page of docs from server
      let snapshot = await getDocs(q);
      let docs = snapshot.docs || [];
      let solutions = docs.map(d => normalizeSolutionData(d)).filter(Boolean);

      try {
        console.debug('[fetchSolutionsFirstPage] server snapshot', {
          postId,
          lowQuota,
          forceServer,
          fromCache: snapshot?.metadata?.fromCache ?? 'unknown',
          size: docs.length,
          total
        });
      } catch (_) { }

      // Fallback path: if count>0 but no docs returned (likely due to missing createdAt/index), retry with updatedAt or without orderBy
      if (total > 0 && docs.length === 0) {
        try {
          const qUpdated = query(
            solutionsRef,
            where('postId', '==', postId),
            orderBy('updatedAt', 'desc'),
            limit(pageSize)
          );
          const snap2 = await getDocs(qUpdated);
          const docs2 = snap2.docs || [];
          const sols2 = docs2.map(d => normalizeSolutionData(d)).filter(Boolean);
          console.warn('[fetchSolutionsFirstPage] fallback(updatedAt) used', { postId, total, size: docs2.length });
          if (docs2.length > 0) {
            snapshot = snap2; docs = docs2; solutions = sols2;
          }
        } catch (e1) {
          try {
            const qNoOrder = query(
              solutionsRef,
              where('postId', '==', postId),
              limit(pageSize)
            );
            const snap3 = await getDocs(qNoOrder);
            const docs3 = snap3.docs || [];
            const sols3 = docs3.map(d => normalizeSolutionData(d)).filter(Boolean);
            console.warn('[fetchSolutionsFirstPage] fallback(no orderBy) used', { postId, total, size: docs3.length, err: e1?.code || e1?.message });
            if (docs3.length > 0) { snapshot = snap3; docs = docs3; solutions = sols3; }
          } catch (e2) {
            console.error('[fetchSolutionsFirstPage] fallback failed', { e1: e1?.code || e1?.message, e2: e2?.code || e2?.message });
          }
        }
      }

      // Legacy schema fallback: some older docs may use projectId instead of postId
      if (docs.length === 0 && !disableCounts) {
        try {
          const legacyCountQ = query(
            solutionsRef,
            where('projectId', '==', postId)
          );
          const legacyCountSnap = await getCountFromServer(legacyCountQ);
          const legacyTotal = Number(legacyCountSnap.data().count || 0);
          if (legacyTotal > 0) {
            const qLegacy = query(
              solutionsRef,
              where('projectId', '==', postId),
              orderBy('updatedAt', 'desc'),
              limit(pageSize)
            );
            const legacySnap = await getDocs(qLegacy);
            const legacyDocs = legacySnap.docs || [];
            const legacySolutions = legacyDocs.map(d => normalizeSolutionData(d)).filter(Boolean);
            console.warn('[fetchSolutionsFirstPage] legacy(projectId) used', {
              postId,
              legacyTotal,
              size: legacyDocs.length
            });
            if (legacyDocs.length > 0) {
              snapshot = legacySnap; docs = legacyDocs; solutions = legacySolutions;
            }
          }
        } catch (eL) {
          console.error('[fetchSolutionsFirstPage] legacy(projectId) fallback error', eL?.code || eL?.message || eL);
        }
      }

      const last = docs.length > 0 ? docs[docs.length - 1] : null;
      const lastCreatedAtMillis = getMillisFromTimestamp(last?.data()?.createdAt);
      const lastCursor = last
        ? { createdAtMillis: lastCreatedAtMillis, id: last.id }
        : null;

      const hasMore = docs.length === pageSize;

      // Prefetch unique author userIds to the usersCache slice
      try {
        const ids = Array.from(new Set((solutions || []).map(s => s.userId).filter(Boolean)));
        if (ids.length > 0) {
          dispatch(fetchUsersByIds({ userIds: ids }));
        }
      } catch (_) { }

      return { postId, solutions, lastCursor, hasMore, pageSize, total };
    } catch (error) {
      console.error('===== FETCH SOLUTIONS FIRST PAGE ERROR =====');
      console.error('Error:', error);
      console.error('=============================================');
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Paginated: Fetch next page using stored cursor
 */
export const fetchMoreSolutionsByPostId = createAsyncThunk(
  'solutions/fetchMoreByPostId',
  async ({ postId }, { getState, rejectWithValue, dispatch }) => {
    try {
      if (!postId) throw new Error('Post ID is required');
      const state = getState();
      const postState = state.solutions?.byPostId?.[postId];
      if (!postState) throw new Error('No existing state for post');
      const { lastCursor, pageSize = 10 } = postState;
      if (!lastCursor || !lastCursor.id) {
        // No cursor -> nothing more to load
        return { postId, solutions: [], lastCursor: null, hasMore: false, total: postState.total ?? 0 };
      }

      const solutionsRef = collection(db, 'solutions');
      const lastDocRef = doc(db, 'solutions', lastCursor.id);
      const lastDocSnap = await getDoc(lastDocRef);
      const q = query(
        solutionsRef,
        where('postId', '==', postId),
        orderBy('createdAt', 'desc'),
        startAfter(lastDocSnap),
        limit(pageSize)
      );

      // Respect force-server flag for pagination too and add diagnostics
      try { await auth?.currentUser?.getIdToken(true); } catch (_) { }
      const forceServer = process.env.REACT_APP_FORCE_SERVER_SOLUTION_READS === 'true';

      let snapshot = await getDocs(q);
      let docs = snapshot.docs || [];
      let solutions = docs.map(d => normalizeSolutionData(d)).filter(Boolean);
      try {
        console.debug('[fetchMoreSolutionsByPostId] server snapshot', {
          postId,
          fromCache: snapshot?.metadata?.fromCache ?? 'unknown',
          size: docs.length,
          forceServer
        });
      } catch (_) { }

      const last = docs.length > 0 ? docs[docs.length - 1] : null;
      const lastCreatedAtMillis = getMillisFromTimestamp(last?.data()?.createdAt);
      const newCursor = last
        ? { createdAtMillis: lastCreatedAtMillis, id: last.id }
        : lastCursor; // Keep previous if none returned

      const hasMore = docs.length === pageSize;

      // Optionally update total count as well (guarded by env flag)
      let total = postState.total ?? undefined;
      const disableCountsMore = process.env.REACT_APP_DISABLE_SOLUTION_COUNTS === 'true';
      if (!disableCountsMore) {
        const countQ = query(
          solutionsRef,
          where('postId', '==', postId)
        );
        const countSnap = await getCountFromServer(countQ);
        total = Number(countSnap.data().count || 0);
      }

      // Prefetch unique author userIds from this page
      try {
        const ids = Array.from(new Set((solutions || []).map(s => s.userId).filter(Boolean)));
        if (ids.length > 0) {
          dispatch(fetchUsersByIds({ userIds: ids }));
        }
      } catch (_) { }

      return { postId, solutions, lastCursor: newCursor, hasMore, total };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
